import React from 'react';
import {
  Grid,
  Card,
  Text,
  Group,
  SimpleGrid,
  Badge,
  Title,
  Stack,
  Progress,
  Avatar,
  Paper,
  RingProgress,
  ThemeIcon,
  Button,
  Timeline,
  Alert,
  Skeleton,
  ActionIcon,
  Menu,
  rem,
} from '@mantine/core';
import {
  IconUsers,
  IconCamera,
  IconActivity,
  IconAlertTriangle,
  IconTrendingUp,
  IconTrendingDown,
  IconChevronRight,
  IconSchool,
  IconBellRinging,
  IconClock,
  IconArrowRight,
  IconUserCheck,
  IconCameraOff,
  IconRefresh,
  IconDots,
  IconEye,
  IconDownload,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile, usePermissions } from '../../hooks/useAuth';
import { useDashboard, useDashboardStats, useActivityFeed } from '../../hooks/useDashboard';
import { formatDistanceToNow } from 'date-fns';
import { SmartLoader } from '../../components/common/LoadingStates';
import NotificationCenter from '../../components/common/NotificationCenter';
import { SystemHealthStatus } from '../../components/admin/SystemHealthStatus';

const MetricCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  trend?: { value: number; positive: boolean };
  color?: string;
  loading?: boolean;
}> = ({ title, value, icon, trend, color = 'blue', loading }) => (
  <Card padding="lg" radius="md" withBorder className="admin-metric-card">
    {loading ? (
      <Skeleton height={80} />
    ) : (
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={500} className="metric-label">
            {title}
          </Text>
          <Text fw={700} size="xl" className="metric-value">
            {value.toLocaleString()}
          </Text>
          {trend && (
            <Group gap={4} mt={2}>
              {trend.positive ? (
                <IconTrendingUp size={14} color="green" />
              ) : (
                <IconTrendingDown size={14} color="red" />
              )}
              <Text size="xs" c={trend.positive ? 'green' : 'red'} fw={500}>
                {trend.positive ? '+' : ''}{trend.value}%
              </Text>
            </Group>
          )}
        </div>
        <div style={{ color: `var(--mantine-color-${color}-6)` }}>
          {icon}
        </div>
      </Group>
    )}
  </Card>
);

const SystemStatusCard: React.FC<{
  title: string;
  value: number;
  color: string;
}> = ({ title, value, color }) => (
  <Paper p="md" radius="md" withBorder>
    <Group justify="space-between" mb="xs">
      <Text size="sm" fw={500}>
        {title}
      </Text>
      <Text size="sm" fw={600} c={color}>
        {value}%
      </Text>
    </Group>
    <Progress value={value} color={color} size="sm" radius="xl" />
  </Paper>
);

// Helper function for time-based greeting
const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { getDisplayName, role } = useUserProfile();
  const { canManageStudents, canManageCameras, canViewReports } = usePermissions();
  
  // Use new dashboard hooks
  const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useDashboard();
  const { data: statsData, isLoading: statsLoading } = useDashboardStats({ period: 'today' });
  const { data: activityData, isLoading: activityLoading } = useActivityFeed({ 
    limit: 10,
    priority: 'high'
  });

  // Extract data from dashboard overview
  const summary = dashboardData?.summary || {
    total_students: 0,
    present_today: 0,
    total_staff: 0,
    active_cameras: 0,
    total_alerts_today: 0,
    unresolved_alerts: 0,
  };

  const systemStatus = dashboardData?.system_status || {
    status: 'unknown',
    message: 'Loading...',
    last_check: new Date().toISOString(),
  };

  // const recentActivities = dashboardData?.recent_activities || [];
  // const recentAlerts = dashboardData?.recent_alerts || [];

  // Extract data from stats
  const stats = statsData || {
    attendance: { today: 0, week_average: 0, month_average: 0, trend: 'stable' },
    safety: { incidents_today: 0, incidents_week: 0, safety_score: 100, high_risk_areas: [] },
    activities: { total_today: 0, by_type: {}, peak_hours: [] },
    cameras: { total: 0, online: 0, offline: 0, recording: 0 },
  };

  // Activity feed data
  const activityFeed = activityData?.activities || [];

  // Calculate system health metrics
  const apiHealth = systemStatus.status === 'healthy' ? 100 : systemStatus.status === 'warning' ? 75 : 50;
  const cameraUptime = stats.cameras?.total > 0 ? (stats.cameras.online / stats.cameras.total) * 100 : 0;
  const aiAccuracy = stats.safety?.safety_score || 100;

  const handleRefresh = () => {
    refetchDashboard();
  };

  return (
    <Stack gap="lg">
      {/* Welcome Header */}
      <Card withBorder radius="lg" p="xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} fw={700} c="white">
              {getTimeBasedGreeting()}, {getDisplayName()}!
            </Title>
            <Text c="rgba(255,255,255,0.8)" size="lg" mt="xs">
              Here's your school overview for {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Text>
          </div>
          <Group gap="md">
            <NotificationCenter buttonProps={{ size: 'lg', variant: 'white' }} />
            {role && (
              <Badge size="lg" variant="white" color="dark">
                {typeof role === 'string' ? role : (role as any).label || 'Admin'}
              </Badge>
            )}
            <ThemeIcon size="xl" radius="xl" variant="white" color="dark">
              <IconUserCheck size={24} />
            </ThemeIcon>
          </Group>
        </Group>
      </Card>

      {/* System Alerts */}
      {(summary.unresolved_alerts > 0 || systemStatus.status !== 'healthy') && (
        <Alert 
          icon={<IconBellRinging size={16} />} 
          title="Important Updates" 
          color={systemStatus.status === 'critical' ? 'red' : 'orange'}
          variant="light"
        >
          <Stack gap="xs">
            {summary.unresolved_alerts > 0 && (
              <Group justify="space-between">
                <Text size="sm">
                  {summary.unresolved_alerts} unresolved alert{summary.unresolved_alerts > 1 ? 's' : ''} require attention
                </Text>
                <Button size="xs" variant="subtle" onClick={() => navigate('/admin/alerts')}>
                  View Alerts
                </Button>
              </Group>
            )}
            {systemStatus.status !== 'healthy' && (
              <Group justify="space-between">
                <Text size="sm">
                  System Status: {systemStatus.message}
                </Text>
                <Text size="xs" c="dimmed">
                  Last checked: {formatDistanceToNow(new Date(systemStatus.last_check), { addSuffix: true })}
                </Text>
              </Group>
            )}
          </Stack>
        </Alert>
      )}

      {/* System Health Status */}
      <SystemHealthStatus />

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        {canManageStudents() && (
          <MetricCard
            title="Students Present Today"
            value={summary.present_today}
            icon={<IconUsers size={32} />}
            color="blue"
            trend={stats.attendance?.trend === 'up' ? 
              { value: 5, positive: true } : 
              stats.attendance?.trend === 'down' ? 
              { value: 5, positive: false } : 
              undefined
            }
            loading={dashboardLoading}
          />
        )}
        
        {canManageCameras() && (
          <MetricCard
            title="Active Cameras"
            value={stats.cameras?.online || 0}
            icon={stats.cameras?.online === stats.cameras?.total ? 
              <IconCamera size={32} /> : 
              <IconCameraOff size={32} />
            }
            color={stats.cameras?.online === stats.cameras?.total ? "green" : "orange"}
            loading={statsLoading}
          />
        )}

        <MetricCard
          title="Activities Today"
          value={stats.activities?.total_today || 0}
          icon={<IconActivity size={32} />}
          color="violet"
          loading={statsLoading}
        />

        <MetricCard
          title="Safety Incidents"
          value={stats.safety?.incidents_today || 0}
          icon={<IconAlertTriangle size={32} />}
          color={stats.safety?.incidents_today === 0 ? "green" : "red"}
          loading={statsLoading}
        />
      </SimpleGrid>

      {/* Quick Navigation Cards */}
      <Card withBorder radius="md" p="lg">
        <Group justify="space-between" mb="md">
          <Title order={3}>Quick Actions</Title>
          <Text size="sm" c="dimmed">Jump to main sections</Text>
        </Group>
        
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {canManageStudents() && (
            <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} 
                   onClick={() => navigate('/admin/students')}>
              <Group>
                <ThemeIcon size="lg" variant="light" color="blue">
                  <IconUsers size={20} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text fw={500}>Manage Students</Text>
                  <Text size="xs" c="dimmed">Enrollment, profiles & face recognition</Text>
                </div>
                <IconArrowRight size={16} />
              </Group>
            </Paper>
          )}

          {canManageCameras() && (
            <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} 
                   onClick={() => navigate('/admin/cameras')}>
              <Group>
                <ThemeIcon size="lg" variant="light" color="green">
                  <IconCamera size={20} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text fw={500}>Camera System</Text>
                  <Text size="xs" c="dimmed">Monitor & configure cameras</Text>
                </div>
                <IconArrowRight size={16} />
              </Group>
            </Paper>
          )}

          <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} 
                 onClick={() => navigate('/admin/schools')}>
            <Group>
              <ThemeIcon size="lg" variant="light" color="violet">
                <IconSchool size={20} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text fw={500}>School Settings</Text>
                <Text size="xs" c="dimmed">Configure school information</Text>
              </div>
              <IconArrowRight size={16} />
            </Group>
          </Paper>
        </SimpleGrid>
      </Card>

      <Grid>
        {/* Activity Feed */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={3}>Activity Feed</Title>
              <Group gap="xs">
                <Badge variant="light" color="blue" size="sm">
                  Live Feed
                </Badge>
                <ActionIcon size="sm" variant="subtle" onClick={handleRefresh}>
                  <IconRefresh size={16} />
                </ActionIcon>
                <Button variant="subtle" size="xs" rightSection={<IconChevronRight size={14} />}
                        onClick={() => navigate('/admin/activities')}>
                  View All
                </Button>
              </Group>
            </Group>

            <SmartLoader
              isLoading={activityLoading}
              isEmpty={activityFeed.length === 0}
              emptyState={
                <Paper p="xl" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                  <Stack align="center" gap="md">
                    <ThemeIcon size="xl" variant="light" color="gray">
                      <IconClock size={24} />
                    </ThemeIcon>
                    <div>
                      <Text fw={500} ta="center">No recent activities</Text>
                      <Text size="sm" c="dimmed" ta="center">Activity feed will appear here as events occur</Text>
                    </div>
                  </Stack>
                </Paper>
              }
            >
              <Timeline active={activityFeed.length} color="blue" lineWidth={2}>
                {activityFeed.map((activity) => (
                  <Timeline.Item 
                    key={activity.id}
                    bullet={
                      activity.actor?.type === 'student' ? (
                        <Avatar size="sm" radius="xl">
                          {activity.actor.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                      ) : (
                        <ThemeIcon size="sm" radius="xl" color={activity.priority === 'urgent' ? 'red' : 'blue'}>
                          {activity.type === 'alert' ? <IconAlertTriangle size={12} /> : <IconActivity size={12} />}
                        </ThemeIcon>
                      )
                    }
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {activity.title}
                        </Text>
                        <Badge
                          size="xs"
                          color={
                            activity.priority === 'urgent' ? 'red' : 
                            activity.priority === 'high' ? 'orange' : 
                            activity.priority === 'medium' ? 'yellow' : 
                            'blue'
                          }
                          variant="dot"
                        >
                          {activity.priority}
                        </Badge>
                      </Group>
                    }
                  >
                    <Text size="xs" c="dimmed" mb={4}>
                      {activity.description}
                    </Text>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </Text>
                      {activity.related_entities?.map((entity) => (
                        <React.Fragment key={entity.id}>
                          <Text size="xs" c="dimmed">•</Text>
                          <Text size="xs" c="dimmed">
                            {entity.name}
                          </Text>
                        </React.Fragment>
                      ))}
                    </Group>
                    <Group gap="xs" mt={8}>
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <ActionIcon size="xs" variant="subtle">
                            <IconDots size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}>
                            View Details
                          </Menu.Item>
                          <Menu.Item leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}>
                            Export
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Timeline.Item>
                ))}
              </Timeline>
            </SmartLoader>
          </Card>
        </Grid.Col>

        {/* System Status */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* System Health Overview */}
            <Card padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Title order={3}>System Health</Title>
                <Badge 
                  variant="light" 
                  color={systemStatus.status === 'healthy' ? 'green' : 
                         systemStatus.status === 'warning' ? 'yellow' : 'red'} 
                  size="sm"
                >
                  {systemStatus.status.charAt(0).toUpperCase() + systemStatus.status.slice(1)}
                </Badge>
              </Group>
              
              <Stack gap="lg">
                <div>
                  <Group justify="center" mb="md">
                    <RingProgress
                      size={120}
                      thickness={8}
                      sections={[
                        { value: Math.round(apiHealth), color: apiHealth > 95 ? 'green' : apiHealth > 80 ? 'yellow' : 'red' },
                      ]}
                      label={
                        <Text ta="center" fw={700} size="lg">
                          {Math.round(apiHealth)}%
                        </Text>
                      }
                    />
                  </Group>
                  <Text ta="center" fw={500} size="sm">Overall System Health</Text>
                </div>

                <SystemStatusCard
                  title="API Performance"
                  value={Math.round(apiHealth)}
                  color={apiHealth > 95 ? "green" : apiHealth > 80 ? "yellow" : "red"}
                />
                
                <SystemStatusCard
                  title="Camera Network"
                  value={Math.round(cameraUptime)}
                  color={cameraUptime > 95 ? "green" : cameraUptime > 80 ? "yellow" : "red"}
                />
                
                <SystemStatusCard
                  title="AI Detection Accuracy"
                  value={Math.round(aiAccuracy)}
                  color={aiAccuracy > 90 ? "green" : aiAccuracy > 75 ? "yellow" : "red"}
                />
              </Stack>
            </Card>

            {/* Safety Summary */}
            <Card padding="lg" radius="md" withBorder>
              <Title order={4} mb="md">Safety Summary</Title>
              
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="blue">
                      <IconUsers size={12} />
                    </ThemeIcon>
                    <Text size="sm">Students Present</Text>
                  </Group>
                  <Text size="sm" fw={600}>{summary.present_today}</Text>
                </Group>

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="green">
                      <IconCamera size={12} />
                    </ThemeIcon>
                    <Text size="sm">Cameras Online</Text>
                  </Group>
                  <Text size="sm" fw={600}>{stats.cameras.online}/{stats.cameras.total}</Text>
                </Group>

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="violet">
                      <IconActivity size={12} />
                    </ThemeIcon>
                    <Text size="sm">Total Activities</Text>
                  </Group>
                  <Text size="sm" fw={600}>{stats.activities.total_today}</Text>
                </Group>

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color={(stats.safety?.incidents_today || 0) > 0 ? "red" : "green"}>
                      <IconAlertTriangle size={12} />
                    </ThemeIcon>
                    <Text size="sm">Safety Incidents</Text>
                  </Group>
                  <Text size="sm" fw={600} c={(stats.safety?.incidents_today || 0) > 0 ? "red" : "green"}>
                    {stats.safety?.incidents_today || 0}
                  </Text>
                </Group>

                {(stats.safety?.high_risk_areas?.length || 0) > 0 && (
                  <>
                    <Text size="sm" fw={500} mt="sm">High Risk Areas</Text>
                    {stats.safety?.high_risk_areas?.map((area) => (
                      <Paper key={area.area_id} p="xs" withBorder>
                        <Group justify="space-between">
                          <Text size="xs">{area.area_name}</Text>
                          <Badge size="xs" color={area.risk_level === 'high' ? 'red' : 'orange'}>
                            {area.incident_count} incidents
                          </Badge>
                        </Group>
                      </Paper>
                    ))}
                  </>
                )}
              </Stack>

              {canViewReports() && (
                <Button 
                  variant="light" 
                  fullWidth 
                  mt="lg" 
                  rightSection={<IconChevronRight size={16} />}
                  onClick={() => navigate('/admin/reports')}
                >
                  View Detailed Reports
                </Button>
              )}
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default Dashboard;